import { Injectable, OnDestroy, inject } from '@angular/core';
import { ApplicationInsights, ITelemetryItem } from '@microsoft/applicationinsights-web';
import { Observable, Subject, takeUntil } from 'rxjs';
import { ConfigService } from 'src/config/config.service';
import { AuthService } from '../../core/services';
import { User } from '../models';

@Injectable({
  providedIn: 'root'
})
export class LoggingService implements OnDestroy {
  private _config = inject(ConfigService);

  private _user: User;
  private _user$: Observable<User>;

  private _appInsights: ApplicationInsights;
  private _authService: AuthService;

  private readonly _ngUnsubscribe$ = new Subject<void>();


  constructor() {}

  init(config: ConfigService, authService: AuthService){
    this._appInsights = new ApplicationInsights({
      config: {
        instrumentationKey: config.applicationInsightsInstrumentationKey,
        enableAutoRouteTracking: true, // option to log all route changes
      }
    });

    this._appInsights?.loadAppInsights();

    const omsTelemetryInitializer = (telemetryItem: ITelemetryItem) => {
      this.addOmsTelemetryProperties(telemetryItem.data);
    };

    this._appInsights?.addTelemetryInitializer(omsTelemetryInitializer);

    this._authService = authService;

    this._user$ = this._authService.authenticationContext;
    this._user$.pipe(
      takeUntil(this._ngUnsubscribe$)
    ).subscribe(user => {
        this._user = user;
    });
  }

  logPageView(name?: string, url?: string, properties?: { [key: string]: any }) { // option to call manually
    this._appInsights?.trackPageView({
      name,
      uri: url,
      properties: {
        ...(properties || {}),
      }
    });
  }
  
  logEvent(name: string, properties?: { [key: string]: any }) {
    this._appInsights?.trackEvent({name}, properties);
  }

  logMetric(name: string, average: number, properties?: { [key: string]: any }) {
    this._appInsights?.trackMetric({ name, average }, properties);
  }

  logException(exception: Error, severityLevel: number = 3) {
    // Custom properties are not added to the exception telemetry from the  telemetry initializer. Bug?
    // Add them manually.
    const customProperties = {};
    this.addOmsTelemetryProperties(customProperties);

    this._appInsights?.trackException({ exception, severityLevel, properties: customProperties });
  }

  logTrace(message: string, properties?: { [key: string]: any }) {
    this._appInsights?.trackTrace({ message}, properties);
  }

   ngOnDestroy() {
    this._ngUnsubscribe$.next();
    this._ngUnsubscribe$.complete();
  }

  private addOmsTelemetryProperties(telemetryProperties: any){
    telemetryProperties['OMS.Application'] = 'OMS.Map.Web';
    telemetryProperties['OMS.Module'] = 'Map';
    telemetryProperties['OMS.UserName'] = this._user?.email;
  };
}
